import { z } from 'zod'

export const appConnection = z.object({
    id: z.string(),
    toolkit: z.string(),
    name: z.string(),
    status: z.enum(['pending', 'connected', 'disconnected']),
})

export const appToolkit = z.object({
    slug: z.string(),
    name: z.string(),
    description: z.string(),
    logo: z.string().url().nullable().optional(),
})

const policyMetadata = {
    allowedDynamicArguments: z.string().array().optional(),
    allowedOutputPaths: z.string().array().optional(),
}

export const appTool = z.object({
    ...policyMetadata,
    slug: z.string(),
    name: z.string(),
    description: z.string(),
    version: z.string(),
    toolkit: z.string(),
    readOnly: z.boolean(),
    inputSchemaJson: z.string(),
})

export const appCatalogPage = z.object({
    items: appToolkit.array(),
    nextCursor: z.string().nullable(),
})

export const appToolPage = z.object({
    items: appTool.array(),
    nextCursor: z.string().nullable(),
})

export const connectionStart = z.object({
    id: z.string(),
    redirectUrl: z.string().url(),
})

export const toolSelection = z.object({
    ...policyMetadata,
    policyJson: z.string().optional(),
    capabilityName: z.string().nullable().optional(),
    friendlyName: z.string().nullable().optional(),
    connectionId: z.string(),
    slug: z.string(),
    version: z.string(),
    fixedArgumentsJson: z.string(),
    name: z.string().optional(),
    description: z.string().optional(),
    inputSchemaJson: z.string().optional(),
    readOnly: z.boolean().optional(),
})

export const toolActivity = z.object({
    name: z.string(),
    status: z.enum(['live', 'simulated', 'failed']),
})

export type AppConnection = z.infer<typeof appConnection>

export type AppToolkit = z.infer<typeof appToolkit>

export type AppTool = z.infer<typeof appTool>

export type ToolSelection = z.infer<typeof toolSelection>

export type ToolActivity = z.infer<typeof toolActivity>

export type ToolTestMode = 'safe' | 'live'

export const configurationQuestion = z.object({
    id: z.string(),
    label: z.string(),
    help: z.string().nullable().optional(),
    type: z.enum(['text', 'single_choice', 'multiple_choice']),
    required: z.boolean(),
    allowOther: z.boolean(),
    options: z.array(
        z.object({
            value: z.string(),
            label: z.string(),
        })
    ),
})

export type ConfigurationQuestion = z.infer<typeof configurationQuestion>

export type ConfigurationAnswer = {
    questionId: string
    values: string[]
    text: string | null
    other: string | null
}

export const toolProposal = z.object({
    actions: z.array(
        z.object({
            tool: toolSelection,
            reason: z.string(),
            effects: z.string(),
        })
    ),
    questions: z.array(configurationQuestion),
    proposalId: z.string().nullable(),
    message: z.string(),
})

export type ToolProposal = z.infer<typeof toolProposal>

export type ConfigureToolsInput = {
    prompt: string
    title: string
    locale: string
    tools: ToolSelection[]
    proposalId?: string
    answers?: ConfigurationAnswer[]
    connectionIds: string[]
    message?: string
    draftTools?: ToolSelection[]
}
